import {Vector2} from "./math.model";

// What the Client uses for a General number the branch leaves out (the RemoteConstantsModel field
// initializers). The Editor shows these and marks them as defaults; keep the two in step.
export const CONSTANT_DEFAULTS = {
    turnsToFog: 0,
    connectionBonusCityPopulation: 0,
    connectionBonusCapitalPopulation: 0,
    capitalCityUnitCapacity: 4,
    additionalCityUnitCapacity: 2,
    minimumDamage: 1,
    minimumRetaliatoryDamage: 1,
    criticalHealthPercent: 50,
    criticalAttackPenalty: 1,
    criticalDefensePenalty: 1,
    goldScore: 5,
    goldPerTurnScore: 20,
    tileInTerritoryScore: 15,
    tileFullVisionScore: 10,
    tileFogVisionScore: 5,
    resumePlaybackInterval: 0.5,
    resumePlaybackOrdersPerPlayer: 5,
    resumePlaybackMaxOrders: 15,
    turnClockWarningFraction: 0.5,
    turnClockCriticalFraction: 0.34,
    turnClockCriticalSeconds: 10,
    turnClockCriticalSecondsAbove: 30,
} satisfies Partial<Record<keyof RemoteConstantsModel, number>>;

export type DefaultedConstant = keyof typeof CONSTANT_DEFAULTS;

export interface RemoteConstantsModel {
    frame: number;
    versionNumber: number;
    startingUnit: string;
    startingBuilding: string;
    startingGold: number;
    capitalCityGoldPerTurn: number;
    cityGoldPerTurn: number;
    // The optional numbers below fall back to CONSTANT_DEFAULTS when absent, as the Client does.
    turnsToFog?: number;
    connectionBonusCityPopulation?: number;
    connectionBonusCapitalPopulation?: number;
    // Unit capacity: the capital's (also the opening cap) and what each further city adds,
    // before levels.
    capitalCityUnitCapacity?: number;
    additionalCityUnitCapacity?: number;
    attackDamageFormula: string;
    minimumDamage?: number;
    retaliatoryDamageFormula: string;
    minimumRetaliatoryDamage?: number;
    criticalHealthPercent?: number;
    criticalAttackPenalty?: number;
    criticalDefensePenalty?: number;

    playerScoreFormula: string;
    // What ONE entity of the kind adds to its owner's score — the source of truth for that
    // kind (SimScore in the Client). Variables: SCORE = the entity's Score field (a tier's
    // Player Score for techs), COST = its gold price, TURNS = research turns (techs only).
    // Blank or absent = SCORE, the pre-formula behaviour. A city is a building with
    // Starting Slots > 0. The Editor previews the result beside each Score field and
    // never writes it back — see utils/scoreFormula.ts.
    unitScoreFormula?: string;
    buildingScoreFormula?: string;
    cityScoreFormula?: string;
    techScoreFormula?: string;
    goldScore?: number;
    goldPerTurnScore?: number;
    tileInTerritoryScore?: number;
    tileFullVisionScore?: number;
    tileFogVisionScore?: number;
    // Resume playback: when a multiplayer or bot game is resumed, the other players' last orders since the
    // player's own last order are played out at this pace (seconds after each has played out, never below
    // 0.25), up to so many per player and so many in all. Presentation only; 0 for either count turns it off.
    resumePlaybackInterval?: number;
    resumePlaybackOrdersPerPlayer?: number;
    resumePlaybackMaxOrders?: number;
    // The in-game turn clock's bands: Warning below a share of the turn; Critical below a share in a turn of
    // up to turnClockCriticalSecondsAbove seconds, and for the last turnClockCriticalSeconds of a longer one.
    // Their colours are styles (uiStyle.turnClock*Color).
    turnClockWarningFraction?: number;
    turnClockCriticalFraction?: number;
    turnClockCriticalSeconds?: number;
    turnClockCriticalSecondsAbove?: number;

    elevationNoise: NoiseMap;
    temperatureNoise: NoiseMap;
    techActionAttributes: Metadata[];
    techPassiveAttributes: Metadata[];
    unitAttributes: Metadata[];
    buildingAttributes: Metadata[];
    buildings: BuildingData[];
    units: UnitData[];
    tiles: TileData[];
    resources: ResourceData[];
    rewards: Metadata[];
    techConfig: TechTreeConfig;
    techTree: TechTreeComponent[];
    // Unit Upgrades researched at the Forge (Rift.Sim.SimUpgrades). Optional: branches authored before
    // it existed omit it; read with `?? []`.
    unitUpgrades?: UnitUpgradeTrack[];
    localization: { [key: string]: { [key: string]: string } };
    imagePacks: { [key: string]: string[][] };
    // building id → ordered construction-frame image keys. Art like imagePacks;
    // the building's logic data holds only the constructionTurns count.
    constructionImagePacks: { [key: string]: string[] };
    images: { [key: string]: ImageMetadata };
    sounds: { [key: string]: SoundMetadata };
    archive: ArchiveData[];
    // Visual style config, carried by Live Preview. Optional: branches authored before it existed omit it and the
    // client falls back to its baked SO defaults. Mirrors StyleSettings (C#).
    styles?: StyleSettings;
}

// r/g/b/a in 0..1, mirroring UnityEngine.Color (see RiftColor in C#).
export interface RiftColor {
    r: number;
    g: number;
    b: number;
    a: number;
}

export interface StyleTintProfile {
    addColor: RiftColor;
    mulColor: RiftColor;
    lerpColor: RiftColor;
}

export interface TileStyle {
    fogColor: RiftColor;
    fogStrength: number;
    fogDesaturation: number;
    fogNoiseScale: number;
    fogNoiseSpeed: number;
    fogSwirl: number;
    fogNoiseAmount: number;
    fogNoiseBrightness: number;
    movementHighlight: StyleTintProfile;
    cloudHighlight: StyleTintProfile;
    fogHighlight: StyleTintProfile;
    cityHighlight: StyleTintProfile;
}

export interface UIStyle {
    unsetPipColor: RiftColor;
    setPipColor: RiftColor;
    interactableColor: RiftColor;
    disabledColor: RiftColor;
    backgroundColor: RiftColor;
    textColor: RiftColor;
    attributeColorInfoBar: RiftColor;
    lockedNode: RiftColor;
    unlockedNode: RiftColor;
    researchedNode: RiftColor;
    purchasedNode: RiftColor;
    // Optional, matching the nullable C# fields: a branch authored before these
    // existed carries no key, and the client reads that as "keep the baked default"
    // rather than as transparent black. Read through DEFAULT_STYLES at the callsite.
    // Population a city owes after a revoked bonus couldn't come off the pip bar.
    deficitPipColor?: RiftColor;
    techTreeLine?: RiftColor;
    lockedNodeBorder?: RiftColor;
    unlockedNodeBorder?: RiftColor;
    researchedNodeBorder?: RiftColor;
    purchasedNodeBorder?: RiftColor;
    lockedNodeText?: RiftColor;
    unlockedNodeText?: RiftColor;
    researchedNodeText?: RiftColor;
    purchasedNodeText?: RiftColor;
    techTreeNodeBorderWidth?: number;
    chartColorLightnessMin?: number;
    chartColorLightnessMax?: number;
    chartColorChromaMax?: number;
    chartDividerWidth?: number;
    // The in-game turn clock's colour per band (when a band starts is General: turnClock*).
    turnClockNormalColor?: RiftColor;
    turnClockWarningColor?: RiftColor;
    turnClockCriticalColor?: RiftColor;
    // The option UI's disc, per OptionCategory: a colour plus a corner radius given as a
    // FRACTION of the disc's shorter side (0.5 = circle, ~0.3 = squircle), which is what
    // lets one value fit both the 37px building-canvas discs and the 370px tile/unit ones.
    produceOptionTint?: RiftColor;
    buildingActionOptionTint?: RiftColor;
    tileActionOptionTint?: RiftColor;
    unitActionOptionTint?: RiftColor;
    selectedCargoOptionTint?: RiftColor;
    produceOptionCornerRadius?: number;
    buildingActionOptionCornerRadius?: number;
    tileActionOptionCornerRadius?: number;
    unitActionOptionCornerRadius?: number;
}

export interface UnitStyle {
    normalHPColor: RiftColor;
    criticalHPColor: RiftColor;
    // Optional, like every style value added after a branch could already exist:
    // read as `?? DEFAULT_STYLES.unitStyle.X` so an unauthored branch shows the
    // baked default rather than 0, which would freeze the animation.
    meleeAttackDuration?: number;       // seconds, melee lunge
    rangedProjectileDuration?: number;  // seconds, projectile flight
    damageTextColor?: RiftColor;        // floating "Tempest -5"
    healTextColor?: RiftColor;          // floating "+3"
}

export interface StyleSettings {
    tileStyle: TileStyle;
    uiStyle: UIStyle;
    unitStyle: UnitStyle;
}

export interface NoiseMap {
    scale: number;
    octaves: number;
    lacunarity: number;
    persistance: number;
}

export interface Metadata {
    uid: string;
    id: string;
    active: boolean;
    values: { [key: string]: number };
    entities: { [key: string]: string };
    // Attribute IDs (not uids) that the host entity must also have for this
    // attribute to function. Currently used to gate Spellcaster-only
    // attributes (ManaShield, every spell). Empty/undefined = no requirement.
    requires?: string[];
    // Tech actions only: the client shows the action's option before it is unlocked,
    // greyed out with a link to its tech. Absent = hidden until unlocked, and only ever
    // written as true. Ticked in the Tech Tree palette. Mirrors Metadata.ShowWhileLocked.
    showWhileLocked?: boolean;
}

export interface ImageMetadata {
    fileName: string;
    pixelMultiplier: number;
    offsetX: number;
    offsetY: number;
    // Authored uniform render scale (1 = default size for the image's
    // kind). Replaces pixelMultiplier as the tuning knob — the client
    // ignores unknown JSON fields until it's wired up there. Absent = 1.
    scale?: number;
    // Natural pixel dimensions of the source image, captured at upload. The
    // export bake computes pixelMultiplier from srcWidth + scale, so sprite
    // world size is resolution-independent (any size from 16x16 to 2Kx2K).
    // Absent on images uploaded before this was captured — the bake falls back
    // to reading the file over the network for those.
    srcWidth?: number;
    srcHeight?: number;
    // LEGACY — no longer written or read. Image names are derived from usage
    // (see utils/imageClassification.ts); authored values in existing branch
    // data are inert and may be pruned from data eventually.
    displayName?: string;
    // Team-colour mask. Absent on every image that doesn't take a team colour.
    mask?: ImageMaskMetadata;
    // Where the subject sits within the canvas, recorded at upload so the client
    // can frame the art as a UI icon. Absent = the client derives it by scanning
    // the alpha channel, so this is a precomputation and never required.
    trim?: ImageTrimMetadata;
}

// The subject's bounding box, as fractions of the image's size measured from the
// BOTTOM-LEFT corner (Unity's texture convention — the editor scans top-down and
// flips on the way in; see opaqueBounds.ts). The raw extent, with no margin added:
// the breathing room around it is the client's TRIM_PADDING, so how large icons
// render can be retuned without re-recording this.
export interface ImageTrimMetadata {
    x: number;
    y: number;
    w: number;
    h: number;
}

// The team-colour mask attached to one image — see ImageMaskSettings in the C#
// model for the full contract. fileName + gain are what the client renders
// with; the rest is authoring state so the tint-mask dialog can re-derive the
// mask after the art changes.
export interface ImageMaskMetadata {
    fileName: string;
    gain?: number;
    targets?: string[];     // "#rrggbb"
    sharpness?: number;
    saturation?: number;
    neutral?: number;
}

export interface SoundMetadata {
    active: boolean;
    fileName: string;
    description: string;
    type: string;
    startDelay: number;
    delay: number;
    volume: number;
    channels: number;
    frequency: number;
}

// BuildingData.attackableBy — the values SimSiege.ParseAttackability reads.
export type BuildingAttackability = 'any' | 'none' | 'siege';
export const BUILDING_ATTACKABILITIES: BuildingAttackability[] = ['siege', 'any', 'none'];
// What a road costs to move along where the tile does not say (TileData.RoadMovementPrice's initializer).
export const DEFAULT_ROAD_MOVEMENT_PRICE = 0.5;

// What a repair costs and takes where the branch does not say (BuildingData.DEFAULT_REPAIR_* in the client).
export const DEFAULT_REPAIR_COST = 2;
export const DEFAULT_REPAIR_TURNS = 1;

export interface BuildingData {
    id: string;
    active: boolean;
    price: number;
    score: number;
    startLevel: number;
    maxLevel: number;
    maxPerCity: number;
    maxPerPlayer: number;
    countsAs?: string;                 // placement-limit alias: also counts against this building id's Max per City/Player (followed transitively; e.g. citadel → temple)
    attributes: string[];
    produceableUnits: string[];
    constructionTurns: number;         // turn-starts spent "under construction" (inert); 0 = instant
    // Siege. Who may attack the building: any unit, none, or siege units only. Absent, it
    // reads as any with health above 0 and siege without; a city is never attackable.
    // Health decides what a hit does: damage, or fire.
    attackableBy?: BuildingAttackability;
    repairCost?: number;               // gold to start repairing it after a siege hit, burning or not; DEFAULT_REPAIR_COST unset
    repairTurns?: number;              // turn-starts a repair takes; 0 = at once; DEFAULT_REPAIR_TURNS unset
    resourceVariants: { [resourceId: string]: string }; // tile resource id → variant building id (e.g. Mine on resource.gold → goldmine)
    rewards: string[][];               // level-up city upgrade choices (separate system)
    passiveRewards: BuildingReward[];  // inline periodic rewards
    properties: BuildingPropertyData;
}

// Combat structures: `health` decides what a hit does. 0 (what a key missing from
// older branch data reads as, and the default for an ordinary building) means no
// hit points: a hit sets it on fire (BuildingData.attackableBy says who may hit it).
// Above 0 makes it a destructible combat structure and the other four start to mean
// something. Mirrors BuildingPropertyData in RemoteConstantsModel.cs — keep the two
// in step.
export interface BuildingPropertyData {
    visionRange: number;
    territoryRange: number;
    startingSlots: number;
    // Optional because a branch authored before they existed has no such keys,
    // and the editor loads branch JSON raw — there is no normalization pass.
    // Read them as `?? 0`, the same value the C# side gives a missing key.
    health?: number;          // 0 = not a combat structure
    attack?: number;
    defense?: number;
    range?: number;           // tiles; 1 reaches only adjacent
    regeneration?: number;    // HP regained after a full turn without attacking
}

export interface BuildingReward {
    uid: string;
    active: boolean;
    trigger: TriggerSpec;
    grant: GrantSpec;
    amountIncreasePerTrigger: number;
}

export type TriggerSpec =
    | { kind: 'OnPlace' }                                // fresh placement (not via upgrade path)
    | { kind: 'OnUpgrade' }                              // created by upgrading from a predecessor building
    | { kind: 'EveryNTurns'; cadence: number; firstTriggerDelay: number }
    | { kind: 'PerAdjacent'; adjacentTo: string }       // entity id of adjacent things to count, every turn
    | { kind: 'PerAdjacentOnce'; adjacentTo: string }   // fires once per matching neighbour ever (permanent gains)
    | { kind: 'TilesExplored'; cloudCount: number }
    | { kind: 'FullTurnOccupied' }                       // checked at TurnEnd
    | { kind: 'BuiltOnTile'; containing: string; cadence: number; firstTriggerDelay: number };
                                                         // placement gate (resource or tile id), then EveryNTurns

export interface GrantSpec {
    kind: 'Gold' | 'Population';
    amount: number;
}

export const TriggerKinds = ['OnPlace', 'OnUpgrade', 'EveryNTurns', 'PerAdjacent', 'PerAdjacentOnce', 'TilesExplored', 'FullTurnOccupied', 'BuiltOnTile'] as const;
export const GrantKinds = ['Gold', 'Population'] as const;

export interface UnitData {
    id: string;
    active: boolean;
    price: number;
    score: number;
    attributes: string[];
    properties: UnitPropertyData;
}

export interface TileData {
    id: string;
    active: boolean;
    rarity: number;
    elevationNoise: Vector2;
    temperatureNoise: Vector2;
    additionalVisionRange: number;
    movementPrice: number;
    roadMovementPrice?: number;        // DEFAULT_ROAD_MOVEMENT_PRICE when absent
    roadIsTrail: boolean;
    supportingTile: string;
    tileType: string;
    canMove: boolean;
    canSettle: boolean;
    canSail: boolean;
    canHide: boolean;
    canFortify: boolean;
    resources: string[];
    buildings: string[];
}

export interface ResourceData {
    id: string;
    active: boolean;
    visionModifier: number;
    startCount: number;
    rarity: number;
    attributes: string[];
}

export interface UnitPropertyData {
    movementSpeed: number;
    range: number;
    health: number;
    attack: number;
    defense: number;
    regeneration: number;
}

export const UnitUpgradeStats = ['attack', 'defense'] as const;
export type UnitUpgradeStat = typeof UnitUpgradeStats[number];

// The three unit types a track usually improves. Any other unit type id works too; only units authored with
// the type take part (SimUpgrades.HasType).
export const UnitUpgradeClasses = ['Infantry', 'Ranged', 'Cavalry'] as const;

// One Unit Upgrade track (RemoteConstantsModel.UnitUpgradeTrack). Its uid is also the localization key of
// its name, with `<uid>.description` for its description.
export interface UnitUpgradeTrack {
    uid: string;
    // A unit type ID ("Infantry"), never its metadata uid.
    unitType: string;
    stat: UnitUpgradeStat;
    levels: UnitUpgradeLevel[];
}

export interface UnitUpgradeLevel {
    price: number;
    turns: number;
    amount: number;
}

export interface TechTreeConfig {
    prices: number[];
    researchTimes: number[];
    playerScores: number[];
}

export interface TechTreeComponent {
    id: string;
    price: number;
    children: string[];
    unlocks: string[];
    // Authored grid cell (integer cells, not pixels — see utils/graphCanvas).
    // Absent means the node has never been placed, and the Tech Tree tab draws
    // it at a computed fallback position until someone moves it or runs
    // Auto-layout.
    x?: number;
    y?: number;
    // Orthogonal routing for the edges from this node to its children. Only
    // edges a designer has actually bent get a record.
    routes?: TechEdgeRoute[];
}

// One dependency edge's authored route, parent -> child.
export interface TechEdgeRoute {
    child: string;
    // Bends in order from the parent towards the child. Empty/absent = a
    // direct connection.
    points: TechEdgePoint[];
    // Which way the final leg into the child leaves the last bend. Default 'h'.
    endAxis?: 'h' | 'v';
}

export interface TechEdgePoint {
    x: number;
    y: number;
    // Which way the leg arriving at this bend leaves the previous point:
    // 'h' turns horizontally first, 'v' vertically first. Default 'h'. Moot
    // when the two points already share an x or a y (a straight segment).
    axis?: 'h' | 'v';
}

export interface ArchiveData {
    fileName: string;
    key: string;
}

export const SoundTypes: string[] = [
    'Music',
    'PlayerTriggered',
    'AmbientEffects'
]
